import withRouter from "Components/Common/withRouter"
import ContinuingEducationForm from "Components/organisms/forms/continuing-education-form"
import ContinuingEducationFields from "Components/organisms/forms/continuing-education-form/types"
import FormPageTemplate from "Components/templates/form-page-template"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import { ContinuingEducationValidation } from "data/continuing-education"
import { FormikHelpers, useFormik } from "formik"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ProfileSliceState } from "slices/auth/profile/reducer"
import { ContinuingEducationSliceState, setContinuingEducationFormFields } from "slices/forms/continuing-education/reducer"
import { getContinuingEducationFormFields, getContinuingEducationRemainingBudget, getContinuingEducationRequest, updateContinuingEducationRequest } from "slices/forms/continuing-education/thunk"
import ContinuingEducationEnvironment from "models/continuing-education-environment"
import ContinuingEducationType from "models/continuing-education-type"
import ContinuingEducationTopic from "models/continuing-education-topic"
import Modal from "Components/molecule/modal";
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import ContinuingEducationRequest from "models/continuing-education"
import { canEdit } from "utils/continuing-education"
import { navigateBackOr, navigateToContinuingEducationDetails } from "utils/navigator"

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({
        employee
    })
)

const selectFormFields = createSelector(
    (state: any) => state.ContinuingEducation,
    ({ environments, types, topics }: ContinuingEducationSliceState) => ({
        environments,
        types,
        topics
    })
)

const EditContinuingEducationPage = (props: any) => {
    document.title = "Edit Continuing Education Request | IbexIQ";
    const { handleOpenManual } = useManual("forms/continuing-education");
    const id = props.router.params.id as string;
    const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
    const [request, setRequest] = useState<ContinuingEducationRequest | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const { employee } = useSelector(selectProfile)
    const { environments, types, topics } = useSelector(selectFormFields)
    const dispatch: any = useDispatch();

    const environmentOptions = useMemo(() => environments.map((e: ContinuingEducationEnvironment) => ({
        value: e.id,
        label: e.description
    })), [environments])

    const typeOptions = useMemo(() => types.map((t: ContinuingEducationType) => ({
        value: t.id,
        label: t.description
    })), [types])

    const topicOptions = useMemo(() => topics.map((t: ContinuingEducationTopic) => ({
        value: t.id,
        label: t.description
    })), [topics])

    const employeeName = employee
        ? (employee.commonName ?? employee.firstName) + " " + employee.lastName
        : ""

    const handleBack = () => navigateBackOr(props.router.navigate, () => navigateToContinuingEducationDetails(props.router.navigate, id))

    const initializePage = async () => {
        setIsLoading(true)

        if (environments.length === 0 || types.length === 0 || topics.length === 0) {
            const formFields = await getContinuingEducationFormFields()
            if (formFields) dispatch(setContinuingEducationFormFields(formFields))
        }

        const res = await getContinuingEducationRequest(id)

        if (!res || !canEdit(res, employee?.id)) {
            handleBack()
            return;
        }

        setRequest(res)
        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [])

    const handleSubmitContinuingEducationForm = (_vals: ContinuingEducationFields, helpers: FormikHelpers<ContinuingEducationFields>) => {
        helpers.setSubmitting(false)
        setShowAreYouSureModal(true)
    }

    const initialValues: ContinuingEducationFields = useMemo(() => ({
        courseName: request?.courseName ?? '',
        courseLink: request?.courseLink ?? '',
        courseHours: request ? Number(request.courseHours) : '',
        courseStartDate: request?.courseStartDate?.toString().split("T")[0] ?? '',
        courseEndDate: request?.courseEndDate?.toString().split("T")[0] ?? '',
        courseDescription: request?.courseDescription ?? '',
        cost: request ? Number(request.cost) : '',
        environmentId: request?.environmentId ?? '',
        typeId: request?.typeId ?? '',
        topicId: request?.topicId ?? ''
    }), [request])

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        isValid,
        setSubmitting,
        isSubmitting
    } = useFormik({
        onSubmit: handleSubmitContinuingEducationForm,
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: ContinuingEducationValidation
    })

    const [remainingBudget, setRemainingBudget] = useState<number | null>(null)

    useEffect(() => {
        if (!values.courseEndDate) {
            setRemainingBudget(null)
            return;
        }

        getContinuingEducationRemainingBudget(values.courseEndDate, [id]).then(setRemainingBudget)
    }, [values.courseEndDate])

    const secondaryApprovalWarning = ((): string | null => {
        if (remainingBudget === null || values.cost === '') return null;
        if (Number(values.cost) <= remainingBudget) return null;

        return `This cost is over your remaining budget of $${remainingBudget.toLocaleString()} CAD — a Secondary Approver will be required.`;
    })()

    const handleModalClose = () => setShowAreYouSureModal(false);

    const handleConfirmResubmit = async () => {
        setSubmitting(true);
        const res = await updateContinuingEducationRequest(id, values)

        if (!res) {
            handleModalClose()
        } else {
            handleBack()
        }
        setSubmitting(false)
    }

    return <FormPageTemplate
        title={"Forms"}
        breadcrumbItem={"Edit Continuing Education Request"}
        actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
        form={<ContinuingEducationForm
            employeeName={employeeName}
            secondaryApprovalWarning={secondaryApprovalWarning}
            environments={environmentOptions}
            types={typeOptions}
            topics={topicOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
        />}
        isLoading={isLoading}
        modal={<Modal
            isOpen={showAreYouSureModal}
            onClose={handleModalClose}
            title={"Confirm"}
            subtitle={"Are you sure you want to resubmit this request? Approval will start over with your manager."}
        >
            <AreYouSureButtons
                disable={isSubmitting}
                acceptButtonStyle={{ width: '150px' }}
                declineButtonStyle={{ width: '150px' }}
                handleAccept={handleConfirmResubmit}
                handleDecline={handleModalClose}
                acceptText={isSubmitting ? "Resubmitting..." : "Resubmit"}
                declineText={"Cancel"}
            />
        </Modal>}
    />
}

export default withRouter(EditContinuingEducationPage);
